/* ============================================================
   INCIPIT · SPOTLIGHT — logique
   ------------------------------------------------------------
   - Reveal au scroll (one-shot) via IntersectionObserver
   - Vidéo autoplay quand visible, pause sinon
   - Page Visibility API (pause si onglet inactif)
   - Re-init dans l'éditeur Shopify
   ============================================================ */

(function () {
  'use strict';

  class Spotlight {
    constructor(el) {
      this.el = el;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.isVisible = false;
      this.hasRevealed = false;

      this.observeReveal();
      this.observeAutoplay();
      this.bindVisibility();
    }

    getActiveVideo() {
      const isMobile = this.mobileQuery.matches;
      const role = isMobile ? 'mobile' : 'desktop';
      return this.el.querySelector(`[data-spot-video="${role}"]`);
    }

    observeReveal() {
      // Si data-animation="off", on révèle direct sans observer
      if (this.el.dataset.animation === 'off') {
        this.el.classList.add('is-revealed');
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.el.classList.add('is-revealed');
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasRevealed) {
              this.hasRevealed = true;
              this.el.classList.add('is-revealed');
              this._revealObserver.unobserve(this.el);
            }
          });
        },
        { threshold: 0.2 }
      );
      this._revealObserver.observe(this.el);
    }

    observeAutoplay() {
      if (!('IntersectionObserver' in window)) {
        this.playVideo();
        return;
      }

      this._playObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.intersectionRatio > 0.25;
            if (this.isVisible) this.playVideo();
            else this.pauseVideo();
          });
        },
        { threshold: [0, 0.25, 0.6] }
      );
      this._playObserver.observe(this.el);
    }

    playVideo() {
      const video = this.getActiveVideo();
      if (!video) return;
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }

    pauseVideo() {
      this.el.querySelectorAll('video').forEach((v) => {
        if (!v.paused) v.pause();
      });
    }

    bindVisibility() {
      this._onVisibility = () => {
        if (document.hidden) this.pauseVideo();
        else if (this.isVisible) this.playVideo();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    destroy() {
      if (this._revealObserver) this._revealObserver.disconnect();
      if (this._playObserver) this._playObserver.disconnect();
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
      this.el._spotlight = null;
    }
  }

  // ===== Init multi-instances =====
  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-spot]').forEach((el) => {
      if (!el._spotlight) {
        el._spotlight = new Spotlight(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // ===== Shopify theme editor =====
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector) initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sec = e.target && e.target.querySelector('[data-spot]');
    if (sec && sec._spotlight) sec._spotlight.destroy();
  });

  document.addEventListener('shopify:section:select', (e) => {
    if (e.target && e.target.querySelector) initAll(e.target);
  });
})();